import { Reveal } from "./Reveal";
import { Instagram } from "lucide-react";
import { useSiteImages } from "@/lib/useSiteImages";

const PORTRAIT_IMG =
  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxjYWxtJTIwd29tYW4lMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODU4NDEyMjZ8MA&ixlib=rb-4.1.0&q=85";

export const About = () => {
  const siteImages = useSiteImages();
  return (
  <section
    id="about"
    data-testid="about-section"
    className="bg-cream-2 px-6 md:px-12 py-28 md:py-40 lg:py-48 overflow-hidden"
  >
    <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-12 md:gap-16 items-center">
      <Reveal className="md:col-span-6" y={40}>
        <div className="relative w-full mb-20 md:mb-0">
          <div className="arch-mask overflow-hidden aspect-[3/4]">
            <img
              src={siteImages.about || PORTRAIT_IMG}
              alt="Amaléya, your guide through Embodied Awakening"
              data-testid="about-portrait"
              className="w-full h-full object-cover"
            />
          </div>
          {siteImages.practice && (
            <div className="arch-mask overflow-hidden absolute -bottom-12 -right-4 md:-right-16 w-48 md:w-72 aspect-[3/4] border-4 border-cream-2 shadow-lg">
              <img
                src={siteImages.practice}
                alt="Amaléya in practice with her community"
                data-testid="about-practice-image"
                className="w-full h-full object-cover"
              />
            </div>
          )}
        </div>
      </Reveal>
      <div className="md:col-span-5 md:col-start-8">
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-amethyst font-medium mb-6">
            Your Guide
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none text-soil">
            Amaléya
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="text-base md:text-lg text-clay leading-relaxed mt-8 space-y-5">
            <p>
              My name is Amaléya Osei, and I am a Community Arts Practitioner,
              Somatic Practitioner, Intuitive Guide, Ceremonial Dancer, and
              Kundalini Yoga Teacher. My work is devoted to supporting people
              through transformation by guiding them back into the wisdom of
              their bodies.
            </p>
            <p>
              For over six years, I have had the honour of supporting
              individuals, families, and communities from diverse backgrounds.
              Before fully answering the call to spiritual service, I worked
              within Health and Social Care, supporting children in care,
              asylum seekers, neurodivergent young people, individuals
              experiencing mental health challenges, and adults living with
              addiction.
            </p>
            <p>
              My own profound spiritual awakening transformed my reality. What
              felt beautiful was also overwhelming, and I remember asking
              myself, “Why is there no guidance for this?” Through movement,
              meditation, ritual, and somatic practice, the body became my
              greatest teacher. I came to realise that awakening was not
              something happening to me. It was something unfolding through me.
            </p>
            <p>
              Embodied Awakening was born from that bridge, where the grounded
              understanding I gained through Health and Social Care met the
              embodied wisdom that emerged through my own initiation. It is a
              space for those navigating awakening and profound transition,
              not to leave the body behind, but to come home to it.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.26}>
          <p className="font-serif italic text-xl md:text-2xl text-amethyst mt-8">
            “Together, we create a space where your awakening can unfold with
            greater ease, awareness, and trust.”
          </p>
        </Reveal>
        <Reveal delay={0.34}>
          <a
            data-testid="about-instagram-link"
            href="https://instagram.com/amaleyaosei"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-10 text-xs uppercase tracking-[0.2em] text-soil hover:text-amethyst transition-colors duration-300"
          >
            <Instagram className="w-4 h-4" />
            @amaleyaosei
          </a>
        </Reveal>
      </div>
    </div>
  </section>
  );
};
